import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Actions, Thunks } from '../../actions';
import deviceStyles from '../../app/device-management/DeviceStyle';
import locationStyles from '../../app/location-management/LocationStyle';
import {
    ALPHA_NUMBERIC,
    IP_ADDRESS,
    ONLY_NUMBER,
    RESTRICT_SPEC_REGEX_FOR_NAME,
    RESTRICT_SPEC_WITHOUT_SPACE_REGEX,
    SERIAL_TYPE,
    TCP_TYPE,
} from '../../constants';
import { communicationType, starBaudData, starBits, starStop, starfishParity } from '../../constants/device-data';
import { useToasts } from '../../data/DataProviders/ToastProvider';
import { pushAppRoute } from '../../data/domain/route-manager';
import { useCurrentAppRoute } from '../../hooks/app-route-hooks';
import { isRecordFound, useSelectedOrganizationData } from '../../hooks/data-hooks';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { Selectors } from '../../selectors';
import { StarfishChildDeviceData } from '../../types/device';
import { useAuthHelper } from '../AuthContextProvider';
import { ContentContainer } from '../ContentContainer';
import { HeaderInfo } from '../HeaderInfo/HeaderInfo';
import { NavigationPromptModal } from '../NavigationPromptModal/NavigationPromptModal';

export const EditStarfishChildDevice: React.FC<{
    childDeviceData: StarfishChildDeviceData | null;
    updateChildDeviceData: Function;
}> = (props) => {
    const history = useHistory();
    const currentAppRoute = useCurrentAppRoute();
    const [ipAddressError, setIPAddressError] = React.useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [communicateType, setCommunicateType] = React.useState<string>('');
    const [unmodifiedDeviceInfo, setUnmodifiedDeviceInfo] = useState<StarfishChildDeviceData | null>(null);
    const [modifiedDeviceInfo, setModifiedDeviceInfo] = useState<StarfishChildDeviceData | null>(null);
    const deviceClasses = deviceStyles();
    const { t } = useLanguageLocale();
    const classes = locationStyles();
    const { addToast } = useToasts();
    const authHelper = useAuthHelper();
    const dispatch = useDispatch();

    const selectedOrganization = useSelectedOrganizationData();
    const childDeviceState = useSelector(Selectors.StarfishDevice.deviceRegistration);

    const navigateToConnectedDeviceProfile = (): void => {
        if (currentAppRoute.type === 'EditStarfishChildDevice')
            pushAppRoute(history, {
                type: 'StarfishChildDeviceProfile',
                deviceId: currentAppRoute.deviceId,
                locationId: currentAppRoute.locationId,
                orgId: currentAppRoute.orgId,
                siteId: currentAppRoute.siteId,
                childDeviceId: currentAppRoute.childDeviceId,
            });
    };

    useEffect(() => {
        const isDataModified = JSON.stringify(unmodifiedDeviceInfo) !== JSON.stringify(modifiedDeviceInfo);
        setIsDirty(isDataModified);
    }, [unmodifiedDeviceInfo, modifiedDeviceInfo]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (props.childDeviceData) {
            setUnmodifiedDeviceInfo(JSON.parse(JSON.stringify(props.childDeviceData)));
            setModifiedDeviceInfo(props.childDeviceData);
            setCommunicateType(props.childDeviceData.communicationType);
        }
    }, [props.childDeviceData]);

    useEffect(() => {
        if (!childDeviceState.starfishgatewayLoading && childDeviceState.starfishgatewaySuccess) {
            addToast('Child Device updated successfully.');
            dispatch(Actions.StarfishDevices.StarfishChildDeviceUpdateReset());
            navigateToConnectedDeviceProfile();
        }
    }, [childDeviceState.starfishgatewayLoading, childDeviceState.starfishgatewaySuccess]); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const isDataModified = JSON.stringify(unmodifiedDeviceInfo) !== JSON.stringify(modifiedDeviceInfo);
        setIsDirty(isDataModified);
    }, [unmodifiedDeviceInfo, modifiedDeviceInfo]); //eslint-disable-line react-hooks/exhaustive-deps

    const onChangeValues = (value: string, type: string): void => {
        if (modifiedDeviceInfo) {
            const deviceNameObj: any = { ...modifiedDeviceInfo };
            deviceNameObj[`${type}`] = value;
            setModifiedDeviceInfo(deviceNameObj);
        }
    };

    const onChangeDeviceIP = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if (e.target.value !== '' && IP_ADDRESS.test(e.target.value)) {
            setIPAddressError(false);
        } else {
            setIPAddressError(true);
        }
        onChangeValues(e.target.value, 'deviceIP');
    };

    const onChangeDeviceName = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if (RESTRICT_SPEC_REGEX_FOR_NAME.test(e.target.value) && modifiedDeviceInfo) {
            onChangeValues(e.target.value, 'name');
        }
    };
    const onChangeDeviceAddress = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if (e.target.value === '' || ONLY_NUMBER.test(e.target.value)) {
            onChangeValues(e.target.value, 'deviceAddress');
        }
    };

    const onChangeComPort = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if ((e.target.value === '' || ALPHA_NUMBERIC.test(e.target.value)) && modifiedDeviceInfo) {
            onChangeValues(e.target.value, 'comPort');
        }
    };

    const onChangePort = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if (e.target.value === '' || ONLY_NUMBER.test(e.target.value)) {
            onChangeValues(e.target.value, 'port');
        }
    };
    const onChangeScanRate = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if (e.target.value === '' || ONLY_NUMBER.test(e.target.value)) {
            onChangeValues(e.target.value, 'scanRate');
        }
    };

    const onChangeSerial = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if ((e.target.value === '' || ALPHA_NUMBERIC.test(e.target.value)) && modifiedDeviceInfo) {
            onChangeValues(e.target.value, 'serial');
        }
    };

    const onChangeMac = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if (e.target.value === '' || RESTRICT_SPEC_WITHOUT_SPACE_REGEX.test(e.target.value)) {
            onChangeValues(e.target.value, 'mac');
        }
    };

    const handleChildDeviceDataChange = (value: string | number | null, type: string): void => {
        if (modifiedDeviceInfo && value) {
            if (type === 'communicationType') {
                setCommunicateType(value.toString());
            }
            const updateChildDeviceObj: any = { ...modifiedDeviceInfo };
            updateChildDeviceObj[`${type}`] = type === 'communicationType' || type === 'parity' ? value : Number(value);
            setModifiedDeviceInfo(updateChildDeviceObj);
        }
    };

    const handleSwitchChange = (value: string | boolean, type: string): void => {
        if (modifiedDeviceInfo && value !== null) {
            const updateChildDeviceObj: any = { ...modifiedDeviceInfo };
            updateChildDeviceObj[`${type}`] = Boolean(value);
            setModifiedDeviceInfo(updateChildDeviceObj);
        }
    };

    const isDisableSerial = (): boolean => {
        if (
            modifiedDeviceInfo &&
            modifiedDeviceInfo.comPort &&
            modifiedDeviceInfo.baudRate &&
            modifiedDeviceInfo.dataBits &&
            modifiedDeviceInfo.parity &&
            modifiedDeviceInfo.stopBits
        ) {
            return !(modifiedDeviceInfo && isDirty);
        }
        return true;
    };

    const isDisableTcp = (): boolean => {
        if (
            modifiedDeviceInfo &&
            modifiedDeviceInfo.deviceIP !== '' &&
            !ipAddressError &&
            modifiedDeviceInfo.deviceAddress &&
            modifiedDeviceInfo.deviceAddress.toString() !== '' &&
            modifiedDeviceInfo.deviceAddress > 0 &&
            modifiedDeviceInfo.port?.toString() !== ''
        ) {
            return !(modifiedDeviceInfo && isDirty);
        }
        return true;
    };

    const isDisable = (): boolean => {
        if (
            !childDeviceState.starfishgatewayLoading &&
            modifiedDeviceInfo &&
            modifiedDeviceInfo.scanRate.toString() !== '' &&
            modifiedDeviceInfo.scanRate >= 1000 &&
            modifiedDeviceInfo.scanRate <= 3600000
        )
            return communicateType.toLowerCase() === SERIAL_TYPE
                ? isDisableSerial()
                : communicateType.toLowerCase() === TCP_TYPE
                ? isDisableTcp()
                : true;
        return true;
    };

    const onSubmit = (): void => {
        setIsDirty(false);
        if (modifiedDeviceInfo && isRecordFound(selectedOrganization)) {
            const updateddeviceObj: StarfishChildDeviceData = { ...modifiedDeviceInfo };
            setModifiedDeviceInfo(updateddeviceObj);
            dispatch(
                Thunks.StarfishChildDevice.StarfishChildDeviceUpdate(
                    modifiedDeviceInfo.deviceId || '',
                    modifiedDeviceInfo.name,
                    modifiedDeviceInfo.serial,
                    modifiedDeviceInfo.scanRate || 0,
                    modifiedDeviceInfo.communicationType,
                    modifiedDeviceInfo.mac,
                    modifiedDeviceInfo.comPort || '',
                    modifiedDeviceInfo.port || 1,
                    modifiedDeviceInfo.baudRate,
                    modifiedDeviceInfo.dataBits,
                    modifiedDeviceInfo.parity || 'None',
                    modifiedDeviceInfo.stopBits,
                    modifiedDeviceInfo.deviceAddress || 1,
                    modifiedDeviceInfo.deviceIP || '',
                    modifiedDeviceInfo.isDisarm,
                    modifiedDeviceInfo.isActive,
                    authHelper,
                    addToast
                )
            );
            props.updateChildDeviceData(updateddeviceObj);
        }
    };

    return (
        <>
            <ContentContainer>
                <Paper style={{ padding: 48 }}>
                    <HeaderInfo title="Edit Starfish Child Device Information" entity="when editing a Child Device" />
                    {props.childDeviceData && modifiedDeviceInfo && (
                        <Box style={{ padding: 8 }}>
                            <Box className={classes.paddingTextbox}>
                                <TextField
                                    id="name"
                                    style={{ width: '100%' }}
                                    variant="filled"
                                    fullWidth
                                    disabled
                                    label="Name"
                                    data-testid="name"
                                    value={modifiedDeviceInfo.name}
                                    helperText={
                                        <Box component="span" display="flex" justifyContent="flex-end">
                                            {modifiedDeviceInfo.name.length}/24
                                        </Box>
                                    }
                                    inputProps={{ minLength: 1, maxLength: 24, 'data-testid': 'name' }}
                                    onChange={(e: any): void => {
                                        onChangeDeviceName(e);
                                    }}
                                />{' '}
                            </Box>
                            <Box className={classes.paddingTextbox}>
                                <TextField
                                    id="deviceType"
                                    style={{ width: '100%' }}
                                    variant="filled"
                                    fullWidth
                                    disabled
                                    label="Supported Device Type"
                                    data-testid="deviceType"
                                    value={modifiedDeviceInfo.deviceType}
                                    inputProps={{ 'data-testid': 'deviceType' }}
                                />{' '}
                            </Box>
                            <Box className={classes.padding8}>
                                <FormControl
                                    variant="filled"
                                    style={{ marginTop: 10 }}
                                    disabled
                                    className={`${deviceClasses.fullWidth}`}
                                >
                                    <InputLabel id="standardData" data-testid="standard-label-dd">
                                        Communication Type
                                    </InputLabel>
                                    <Select
                                        label="communcation type"
                                        value={modifiedDeviceInfo.communicationType}
                                        inputProps={{ 'data-testid': 'communicationType-dd', width: '100%' }}
                                        onChange={(e): void => {
                                            handleChildDeviceDataChange(e.target.value, 'communicationType');
                                        }}
                                    >
                                        {communicationType.map(
                                            (data: { key: string; value: string }, index: number) => (
                                                <MenuItem key={index} value={data.key} data-testid={`comtype${index}`}>
                                                    {data.value}
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box className={`${classes.paddingTextbox}`}>
                                <TextField
                                    inputProps={{ 'data-testid': 'scanrate', maxLength: 7 }}
                                    className={deviceClasses.fullWidth}
                                    required
                                    variant="filled"
                                    value={modifiedDeviceInfo.scanRate}
                                    error={
                                        !(modifiedDeviceInfo.scanRate >= 1000 && modifiedDeviceInfo.scanRate <= 3600000)
                                    }
                                    id="scanRate"
                                    label="Scan Rate (In Milliseconds)"
                                    onChange={(e: any): void => onChangeScanRate(e)}
                                    helperText="Valid Scan Rate is between 1000 ms - 3600000 ms."
                                />
                            </Box>
                            <Box className={classes.paddingTextbox}>
                                <TextField
                                    id="serial"
                                    style={{ width: '100%' }}
                                    variant="filled"
                                    label="Serial"
                                    data-testid="serial-number"
                                    value={modifiedDeviceInfo.serial}
                                    inputProps={{ maxLength: 255, 'data-testid': 'serial' }}
                                    onChange={(e: any): void => {
                                        onChangeSerial(e);
                                    }}
                                />
                            </Box>

                            {communicateType.toLowerCase() === TCP_TYPE && (
                                <>
                                    <Box className={classes.paddingTextbox}>
                                        <TextField
                                            id="mac"
                                            style={{ width: '100%' }}
                                            variant="filled"
                                            label="Mac"
                                            data-testid="mac"
                                            value={modifiedDeviceInfo.mac}
                                            inputProps={{ maxLength: 255 }}
                                            onChange={(e: any): void => {
                                                onChangeMac(e);
                                            }}
                                        />
                                    </Box>
                                    <Box className={classes.paddingTextbox}>
                                        <TextField
                                            id="deviceIp"
                                            style={{ width: '100%' }}
                                            variant="filled"
                                            label="Device IP"
                                            required
                                            data-testid="deviceIp"
                                            error={ipAddressError}
                                            helperText={ipAddressError ? 'Invalid Device IP Address' : ''}
                                            value={modifiedDeviceInfo.deviceIP}
                                            inputProps={{ maxLength: 50 }}
                                            onChange={(e: any): void => {
                                                onChangeDeviceIP(e);
                                            }}
                                        />
                                    </Box>
                                    <Box className={classes.paddingTextbox}>
                                        <TextField
                                            inputProps={{ 'data-testid': 'device-address', maxLength: 3 }}
                                            required
                                            fullWidth
                                            id="deviceAddress"
                                            label={'Device Address'}
                                            variant="filled"
                                            error={
                                                !(
                                                    modifiedDeviceInfo.deviceAddress &&
                                                    modifiedDeviceInfo.deviceAddress > 0 &&
                                                    modifiedDeviceInfo.deviceAddress < 255
                                                )
                                            }
                                            helperText="Valid Device address are between 1 - 255."
                                            value={modifiedDeviceInfo.deviceAddress}
                                            onChange={(e: any): void => {
                                                onChangeDeviceAddress(e);
                                            }}
                                        />
                                    </Box>
                                    <Box className={classes.paddingTextbox}>
                                        <TextField
                                            inputProps={{ 'data-testid': 'port', maxLength: 5 }}
                                            fullWidth
                                            required
                                            variant="filled"
                                            id="port"
                                            label="Port"
                                            error={
                                                !(
                                                    modifiedDeviceInfo.port &&
                                                    Number(modifiedDeviceInfo.port) > 0 &&
                                                    Number(modifiedDeviceInfo.port) < 65535
                                                )
                                            }
                                            helperText="Valid Port are between 0 - 65535."
                                            value={modifiedDeviceInfo.port}
                                            onChange={(e): void => {
                                                onChangePort(e);
                                            }}
                                        />
                                    </Box>
                                </>
                            )}
                            {communicateType.toLowerCase() === SERIAL_TYPE && (
                                <>
                                    <Box
                                        className={`${classes.padding8}`}
                                        display="flex"
                                        justifyContent="space-between"
                                    >
                                        <Box style={{ width: '50%', paddingLeft: 0 }} className={`${classes.padding8}`}>
                                            <FormControl variant="filled" required className={deviceClasses.fullWidth}>
                                                <TextField
                                                    id="comPort"
                                                    style={{ width: '100%' }}
                                                    variant="filled"
                                                    label="Com Port"
                                                    required
                                                    data-testid="comPort"
                                                    value={modifiedDeviceInfo.comPort}
                                                    inputProps={{ 'data-testid': 'comPort', maxLength: 10 }}
                                                    onChange={(e: any): void => {
                                                        onChangeComPort(e);
                                                    }}
                                                />
                                            </FormControl>
                                        </Box>

                                        <Box
                                            style={{ width: '50%', paddingLeft: 8, paddingRight: 0 }}
                                            className={`${classes.padding8}`}
                                        >
                                            <FormControl variant="filled" required className={deviceClasses.fullWidth}>
                                                <TextField
                                                    inputProps={{ 'data-testid': 'device-address', maxLength: 3 }}
                                                    required
                                                    fullWidth
                                                    id="deviceAddress"
                                                    label={'Device Address'}
                                                    variant="filled"
                                                    error={
                                                        !(
                                                            modifiedDeviceInfo.deviceAddress &&
                                                            modifiedDeviceInfo.deviceAddress > 0 &&
                                                            modifiedDeviceInfo.deviceAddress < 255
                                                        )
                                                    }
                                                    helperText="Valid Device address are between 1 - 255."
                                                    value={modifiedDeviceInfo.deviceAddress}
                                                    onChange={(e: any): void => {
                                                        onChangeDeviceAddress(e);
                                                    }}
                                                />
                                            </FormControl>
                                        </Box>
                                    </Box>

                                    <Box
                                        className={`${classes.padding8}`}
                                        display="flex"
                                        justifyContent="space-between"
                                    >
                                        <Box style={{ width: '50%', paddingLeft: 0 }} className={`${classes.padding8}`}>
                                            <FormControl variant="filled" required className={deviceClasses.fullWidth}>
                                                <InputLabel id="baudRateData" data-testid="baud-rate">
                                                    Baud Rate
                                                </InputLabel>
                                                <Select
                                                    label="baudRate"
                                                    value={modifiedDeviceInfo.baudRate}
                                                    required
                                                    inputProps={{ 'data-testid': 'baud-rate-dd' }}
                                                    onChange={(e): void => {
                                                        handleChildDeviceDataChange(e.target.value, 'baudRate');
                                                    }}
                                                >
                                                    {starBaudData.map(
                                                        (data: { key: string; value: string }, index: number) => (
                                                            <MenuItem
                                                                key={index}
                                                                value={data.key}
                                                                data-testid={`baud-data${index}`}
                                                            >
                                                                {data.value}
                                                            </MenuItem>
                                                        )
                                                    )}
                                                </Select>
                                            </FormControl>
                                        </Box>

                                        <Box
                                            style={{ width: '50%', paddingRight: 0 }}
                                            className={`${classes.padding8}`}
                                        >
                                            <FormControl variant="filled" required fullWidth>
                                                <InputLabel id="bits" data-testid="bits">
                                                    Data Bits
                                                </InputLabel>
                                                <Select
                                                    label="bits"
                                                    value={modifiedDeviceInfo.dataBits}
                                                    required
                                                    inputProps={{ 'data-testid': 'bits-dd', width: '50%' }}
                                                    onChange={(e): void => {
                                                        handleChildDeviceDataChange(e.target.value, 'dataBits');
                                                    }}
                                                >
                                                    {starBits.map(
                                                        (data: { key: number; value: string }, index: number) => (
                                                            <MenuItem
                                                                key={index}
                                                                value={data.key}
                                                                data-testid={`bits${index}`}
                                                            >
                                                                {data.value}
                                                            </MenuItem>
                                                        )
                                                    )}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                    </Box>
                                    <Box
                                        className={`${classes.padding8}`}
                                        display="flex"
                                        justifyContent="space-between"
                                    >
                                        <Box style={{ width: '50%', paddingLeft: 0 }} className={`${classes.padding8}`}>
                                            <FormControl variant="filled" required fullWidth>
                                                <InputLabel id="parityData" data-testid="parity">
                                                    Parity
                                                </InputLabel>
                                                <Select
                                                    label="parity"
                                                    value={modifiedDeviceInfo.parity}
                                                    required
                                                    inputProps={{ 'data-testid': 'parity-dd', width: '50%' }}
                                                    onChange={(e): void => {
                                                        handleChildDeviceDataChange(e.target.value, 'parity');
                                                    }}
                                                >
                                                    {starfishParity.map(
                                                        (data: { key: string; value: string }, index: number) => (
                                                            <MenuItem
                                                                key={index}
                                                                value={data.key}
                                                                data-testid={`parity${index}`}
                                                            >
                                                                {data.value}
                                                            </MenuItem>
                                                        )
                                                    )}
                                                </Select>
                                            </FormControl>
                                        </Box>

                                        <Box
                                            style={{ width: '50%', paddingRight: 0 }}
                                            className={`${classes.padding8}`}
                                        >
                                            <FormControl variant="filled" required fullWidth>
                                                <InputLabel id="stopBits" data-testid="stopbits">
                                                    Stop Bits
                                                </InputLabel>
                                                <Select
                                                    label="stopBits"
                                                    value={modifiedDeviceInfo.stopBits}
                                                    required
                                                    inputProps={{ 'data-testid': 'stop-bits-dd', width: '50%' }}
                                                    onChange={(e): void => {
                                                        handleChildDeviceDataChange(e.target.value, 'stopBits');
                                                    }}
                                                >
                                                    {starStop.map(
                                                        (data: { key: number; value: string }, index: number) => (
                                                            <MenuItem
                                                                key={index}
                                                                value={data.key}
                                                                data-testid={`stop-bits${index}`}
                                                            >
                                                                {data.value}
                                                            </MenuItem>
                                                        )
                                                    )}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                    </Box>
                                    <Box
                                        className={`${classes.padding8}`}
                                        display="flex"
                                        justifyContent="space-between"
                                    ></Box>
                                </>
                            )}

                            <Box className={`${classes.padding8}`} display="flex" justifyContent="space-between">
                                <Box style={{ width: '50%', paddingLeft: 0 }}>
                                    <FormControl variant="filled" required fullWidth>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={modifiedDeviceInfo.isDisarm}
                                                    style={{ margin: '6px 4px 4px 10px', padding: '0px' }}
                                                    onChange={(e): void => {
                                                        handleSwitchChange(e.target.checked, 'isDisarm');
                                                    }}
                                                />
                                            }
                                            label={<Typography>DisArm</Typography>}
                                        />
                                    </FormControl>
                                </Box>

                                <Box style={{ width: '50%', paddingRight: 0 }}>
                                    <FormControl variant="filled" required fullWidth>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={modifiedDeviceInfo.isActive}
                                                    style={{ margin: '6px 4px 4px 10px', padding: '0px' }}
                                                    onChange={(e): void => {
                                                        handleSwitchChange(e.target.checked, 'isActive');
                                                    }}
                                                />
                                            }
                                            label={<Typography>Enabled</Typography>}
                                        />
                                    </FormControl>
                                </Box>
                            </Box>
                        </Box>
                    )}
                    <Box
                        className={classes.iconRightEnd}
                        style={{ display: 'flex', justifyContent: 'flex-end', padding: '24px 8px' }}
                    >
                        <Button
                            style={{ marginRight: 16 }}
                            variant="outlined"
                            color="primary"
                            data-testid="cancel-device"
                            onClick={navigateToConnectedDeviceProfile}
                        >
                            {t('ACTIONS.CANCEL')}
                        </Button>
                        <Button
                            startIcon={
                                childDeviceState.starfishgatewayLoading ? (
                                    <CircularProgress size="1rem" style={{ color: 'white' }} />
                                ) : (
                                    <></>
                                )
                            }
                            variant="contained"
                            disabled={isDisable()}
                            color="primary"
                            data-testid="edit-device-button"
                            onClick={onSubmit}
                        >
                            {t('ACTIONS.SUBMITCHANGES')}
                        </Button>
                    </Box>
                </Paper>
            </ContentContainer>
            <NavigationPromptModal isDirty={isDirty} />
        </>
    );
};
